import { TFunction } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  IconAward01,
  IconBookOpen02,
  IconBuilding03,
  IconCalendarCheck02,
  IconCalendarHeart02,
  IconChevronDown,
  IconClipboardPlus,
  IconCode02,
  IconCoins02,
  IconCoinsHand,
  IconDotpoints02,
  IconFileAttachment02,
  IconFileLock02,
  IconFileSearch02,
  IconFileShield02,
  IconHome02,
  IconInbox01,
  IconLayersThree01,
  IconLogOut01,
  IconPackage,
  IconReceiptCheck,
  IconSettings04,
  IconShield01,
  IconShoppingCart01,
  IconTicket02,
  IconTool02,
  IconUserCircle,
  IconUserDown01,
  IconUserDown02,
  IconUserMinus02,
  IconUserRight01,
  IconUserUp02,
  IconUsers01,
} from "@pentabd/icons";
import { Select, Sidebar, Switch, Text } from "@pentabd/ui";

import { getNameByPath } from "./sideBarMenu";
import { getLogo } from "../Logo";

import { useAuth } from "@hooks/useAuth";
import { useLanguage } from "@hooks/useLanguage";
import { useUser } from "@hooks/useUser";

import { ROUTES } from "@constants/routes";
import { getAccess } from "@helpers/permissionController";

import { getStorage, removeStorage, setStorage } from "@utils/local-store";

import { modules } from "../../../Home/components/ModuleCards/const";

import "./style.scss";

import { DashboardContext, DashboardContextType } from "../../../../router";
import { modulesMapper } from "@helpers/modulesMapper";

const {
  HOME,
  TICKETS: {
    E_TICKETS,
    MY_TICKETS,
    REQUESTED_TICKETS,
    ASSIGNED_TICKETS,
    SETTINGS,
  },
  PROCUREMENT: {
    ANNUAL_PROCUREMENT_PLAN,
    DEMANDS,
    PROCUREMENTS,
    VENDORS,
    COMMITTEE,
  },
  TASK_MANAGEMENT: {
    ALL_TASKS,
    OWN_TASKS,
    SHARED_TASKS,
    ASSIGNED_TASKS,
    ASSIGNED_SUB_TASKS,
    SHARED_SUB_TASKS,
    ARCHIVED_TASKS,
    SETTINGS: TASK_MANAGEMENT_SETTINGS,
  },
  HRM: {
    EMPLOYEES,
    ORG_CHART,
    POSITIONS,
    SENIORITY,
    PROMOTION_CANDIDATES,
    PERMANENT,
    DEPUTATION,
    RELEASE,
    TRANSFER_CANDIDATES,
    TRANSFERS,
    PROMOTION,
    RETIREMENTS,
    RESIGNATION,
    LEAVE,
    PUNISHMENT,
    APR,
    ACR,
    DOSSIER,
    PROVIDENT_FUND,
    SETTINGS: HRM_SETTINGS,
    MANPOWER_REPORTS,
    PROMOTION_REPORTS,
    PRL_REPORTS,
    REWARD_REPORTS,
    JOB_REPORTS,
    MANPOWER_SUMMARY_REPORTS,
    MANPOWER_DETAILS_REPORTS,
    MANPOWER_RECRUIT_REPORTS,
    PENSION_REPORTS,
    RELEASE_REPORTS,
    RESIGNATION_REPORTS,
    RETIREMENT_REPORTS,
    YEARLY_REPORTS,
    ACR_REPORTS,
    DECEASED_LIST,
    DECEASED,
    TERMINATION_LIST,
    TERMINATION,
    EXTRA_POSITION_INFORMATIONS,
    INTERCHANGE_TRANSFERS,
    POSITION_TREE,
    FILE_INQUIRY,
  },
  INVENTORY: {
    EMPLOYEES: INVENTORY_EMPLOYEES,
    STOCK,
    PROCUREMENTS: INVENTORY_PROCUREMENTS,
    BUDGET,
    FORECASTING,
    REQUISITION,
    ROOM_CONFIGURATION,
    CONFERENCE_ROOM,
    WARRANTY,
    MAINTENANCE,
    SETTINGS: INVENTORY_SETTINGS,
    SOFTWARE_INVENTORY,
  },
} = ROUTES;

const LANGUAGE = {
  BANGLA: "bn",
  ENGLISH: "en",
};

interface ModuleMenuMapType {
  [key: string]: any;
}

const body = (
  t: TFunction<"translation", undefined>,
  roles: string[],
  currentModule: string,
): any => {
  const moduleMenuMap: ModuleMenuMapType = {
    HRM: [
      {
        ...(getAccess(roles, "EMPLOYEE_VIEW_PAGE") && {
          icon: <IconUsers01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.EMPLOYEES.EMPLOYEE"),
          link: EMPLOYEES,
        }),
      },
      {
        ...((getAccess(roles, "CAN_VIEW_ORG_CHART") ||
          getAccess(roles, "POSITION_VIEW_PAGE") ||
          getAccess(roles, "SENIORITY_LIST_PAGE") ||
          getAccess(roles, "PROMOTION_INITIATE") ||
          getAccess(roles, "PERMANENT_LIST_PAGE") ||
          getAccess(roles, "TRANSFER_INITIATE")) && {
          icon: <IconLayersThree01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.ORGANIZATION.ORGANIZATION"),

          subMenu: [
            {
              ...(getAccess(roles, "CAN_VIEW_ORG_CHART") && {
                name: t("SIDEBAR.ORGANIZATION.CHART"),
                link: ORG_CHART,
              }),
            },
            {
              ...(getAccess(roles, "POSITION_VIEW_PAGE") && {
                name: t("SIDEBAR.ORGANIZATION.POSITION"),
                link: POSITIONS,
              }),
            },
            {
              ...(getAccess(roles, "EMPLOYEE_POSITION_LIST_PAGE") && {
                name: t("SIDEBAR.ORGANIZATION.POSITION_TREE"),
                link: POSITION_TREE,
              }),
            },
            {
              ...(getAccess(roles, "SENIORITY_LIST_PAGE") && {
                name: t("SIDEBAR.ORGANIZATION.SENIORITY"),
                link: SENIORITY,
              }),
            },
            {
              ...(getAccess(roles, "PROMOTION_INITIATE") && {
                name: t("SIDEBAR.ORGANIZATION.PROMOTION"),
                link: PROMOTION_CANDIDATES,
              }),
            },
            {
              ...(getAccess(roles, "PERMANENT_LIST_PAGE") && {
                name: t("SIDEBAR.ORGANIZATION.PERMANENT"),
                link: PERMANENT,
              }),
            },
            {
              ...(getAccess(roles, "TRANSFER_INITIATE") && {
                name: t("SIDEBAR.ORGANIZATION.TRANSFER"),
                link: TRANSFER_CANDIDATES,
              }),
            },
            {
              ...(getAccess(roles, "RELEASE_UPDATE") && {
                name: t("SIDEBAR.ORGANIZATION.DEPUTATION"),
                link: DEPUTATION,
              }),
            },
            {
              ...(getAccess(roles, "DECEASED_INITIATE") && {
                name: t("SIDEBAR.ORGANIZATION.DECEASED"),
                link: DECEASED_LIST,
              }),
            },
            {
              ...(getAccess(roles, "TERMINATION_INITIATE") && {
                name: t("SIDEBAR.ORGANIZATION.TERMINATION"),
                link: TERMINATION_LIST,
              }),
            },
            {
              ...(getAccess(roles, "POSITION_VIEW_PAGE") && {
                name: t("SIDEBAR.ORGANIZATION.EXTRA_POSITION_INFORMATIONS"),
                link: EXTRA_POSITION_INFORMATIONS,
              }),
            },
          ],
        }),
      },
      {
        ...(getAccess(roles, "TRANSFER_LIST_PAGE") && {
          icon: <IconUserRight01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.TRANSFERS"),
          link: TRANSFERS,
        }),
      },
      {
        ...(getAccess(roles, "TRANSFER_LIST_PAGE") && {
          icon: <IconUserCircle fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INTERCHANGE_TRANSFER"),
          link: INTERCHANGE_TRANSFERS,
        }),
      },
      {
        ...(getAccess(roles, "PROMOTION_LIST_VIEW_PAGE") && {
          icon: <IconUserUp02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROMOTION"),
          link: PROMOTION,
        }),
      },
      {
        ...(getAccess(roles, "RETIREMENT_LIST_PAGE") && {
          icon: <IconUserMinus02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.RETIREMENT"),
          link: RETIREMENTS,
        }),
      },
      {
        ...(getAccess(roles, "RESIGNATION_LIST_PAGE") && {
          icon: <IconUserDown01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.RESIGNATION"),
          link: RESIGNATION,
        }),
      },
      {
        ...(getAccess(roles, "RELEASE_UPDATE") && {
          icon: <IconUserDown02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.RELEASE"),
          link: RELEASE,
        }),
      },
      {
        ...(getAccess(roles, "DECEASED_LIST_PAGE") && {
          icon: <IconUserRight01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.DECEASED"),
          link: DECEASED,
        }),
      },

      {
        ...(getAccess(roles, "TERMINATION_LIST_PAGE") && {
          icon: <IconUserRight01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.TERMINATION"),
          link: TERMINATION,
        }),
      },
      // {
      //   icon: <IconCalendarCheck02 fill="subtitle2" size="24" />,
      //   name: t("SIDEBAR.ATTENDANCE"),
      //   link: CENTRAL_ATTENDANCE,
      // },
      {
        ...(getAccess(roles, "LEAVE_VIEW_PAGE") && {
          icon: <IconCalendarHeart02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.LEAVE"),
          link: LEAVE,
        }),
      },
      {
        ...(getAccess(roles, "PUNISHMENT_LIST_PAGE") && {
          icon: <IconFileSearch02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PUNISHMENT"),
          link: PUNISHMENT,
        }),
      },
      {
        ...(getAccess(roles, "APR_LIST_PAGE") && {
          icon: <IconFileSearch02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.APR"),
          link: APR,
        }),
      },
      {
        ...(getAccess(roles, "ACR_LIST_PAGE") && {
          icon: <IconFileLock02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.ACR"),
          link: ACR,
        }),
      },
      {
        ...(getAccess(roles, "DOSSIER_LIST_PAGE") && {
          icon: <IconFileLock02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.DOSSIER"),
          link: DOSSIER,
        }),
      },
      {
        ...(getAccess(roles, "GPF_LIST_PAGE") && {
          icon: <IconFileShield02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROVIDENT_FUND"),
          link: PROVIDENT_FUND,
        }),
      },
      {
        ...(getAccess(roles, "CAN_VIEW_ALL_FILE_INQUIRY") && {
          icon: <IconFileAttachment02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.FILE_INQUIRY"),
          link: FILE_INQUIRY,
        }),
      },

      {
        ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
          icon: <IconBookOpen02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.REPORTS.REPORTS"),

          subMenu: [
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.MANPOWER"),
                link: MANPOWER_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.PROMOTION"),
                link: PROMOTION_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.PRL"),
                link: PRL_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.PENSION"),
                link: PENSION_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.RESIGNATION"),
                link: RESIGNATION_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.RETIREMENT"),
                link: RETIREMENT_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.RELEASE"),
                link: RELEASE_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.REWARD"),
                link: REWARD_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.JOB"),
                link: JOB_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.MANPOWER_SUMMARY"),
                link: MANPOWER_SUMMARY_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.MANPOWER_DETAILS"),
                link: MANPOWER_DETAILS_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.MANPOWER_RECRUIT"),
                link: MANPOWER_RECRUIT_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.YEARLY_REPORTS"),
                link: YEARLY_REPORTS,
              }),
            },
            {
              ...(getAccess(roles, "CAN_DOWNLOAD_ALL_REPORTS") && {
                name: t("SIDEBAR.REPORTS.ACR_REPORT"),
                link: ACR_REPORTS,
              }),
            },
          ],
        }),
      },
      {
        ...(getAccess(roles, "CAN_HRM_SETTINGS") && {
          icon: <IconSettings04 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.HRM_SETTINGS"),
          link: HRM_SETTINGS,
        }),
      },
    ],

    INVENTORY: [
      {
        ...(getAccess(roles, "INVENTORY_EMPLOYEE_PAGE") && {
          icon: <IconUsers01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.EMPLOYEES.EMPLOYEES"),
          link: INVENTORY_EMPLOYEES,
        }),
      },
      {
        ...(getAccess(roles, "INVENTORY_STOCK_PAGE") && {
          icon: <IconFileAttachment02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.STOCK"),
          link: STOCK,
        }),
      },
      {
        ...(getAccess(roles, "INVENTORY_PROCUREMENT_PAGE") && {
          icon: <IconClipboardPlus fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.REGISTER.PROCUREMENTS"),
          link: INVENTORY_PROCUREMENTS,
        }),
      },
      {
        ...(getAccess(roles, "INVENTORY_BUDGET_PAGE") && {
          icon: <IconCoinsHand fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.BUDGET"),
          link: BUDGET,
        }),
      },
      {
        ...(getAccess(roles, "INVENTORY_FORECASTING_PAGE") && {
          icon: <IconCoins02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.FORECASTING"),
          link: FORECASTING,
        }),
      },

      {
        icon: <IconInbox01 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.REQUISITION"),
        link: REQUISITION,
      },
      {
        ...(getAccess(roles, "INVENTORY_ROOM_PAGE") && {
          icon: <IconBuilding03 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.ROOM.ROOM"),
          link: ROOM_CONFIGURATION,
        }),
      },
      {
        icon: <IconCalendarCheck02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.ROOM.CONFERENCE_ROOM"),
        link: CONFERENCE_ROOM,
      },
      {
        ...(getAccess(roles, "INVENTORY_WARRANTY_PAGE") && {
          icon: <IconShield01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.WARRANTY"),
          link: WARRANTY,
        }),
      },
      {
        // ...(getAccess(roles, "INVENTORY_MAINTENANCE_PAGE") && {
        icon: <IconTool02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.MAINTENANCE"),
        link: MAINTENANCE,
        // }),
      },
      {
        ...(getAccess(roles, "INVENTORY_SETTINGS_PAGE") && {
          icon: <IconSettings04 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.SETTINGS"),
          link: INVENTORY_SETTINGS,
        }),
      },
    ],

    PROCUREMENT: [
      {
        ...(getAccess(roles, "PROCUREMENT_STORE_IN_CHARGE") && {
          icon: <IconReceiptCheck fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROCUREMENT.ANNUAL_PROCUREMENT_PLAN"),
          link: ANNUAL_PROCUREMENT_PLAN,
        }),
      },
      {
        ...(getAccess(roles, "PROCUREMENT_STORE_IN_CHARGE") && {
          icon: <IconShoppingCart01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROCUREMENT.DEMANDS"),
          link: DEMANDS,
        }),
      },
      {
        ...(getAccess(roles, "PROCUREMENT_STORE_IN_CHARGE") && {
          icon: <IconClipboardPlus fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROCUREMENT.PROCUREMENTS"),
          link: PROCUREMENTS,
        }),
      },
      {
        ...(getAccess(roles, "PROCUREMENT_HR") && {
          icon: <IconPackage fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROCUREMENT.VENDORS"),
          link: VENDORS,
        }),
      },
      {
        ...(getAccess(roles, "PROCUREMENT_HR") && {
          icon: <IconAward01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.PROCUREMENT.COMMITTEE"),
          link: COMMITTEE,
        }),
      },
    ],

    TM: [
      {
        icon: <IconDotpoints02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.TASK_MANAGEMENT.TASKS"),

        subMenu: [
          {
            ...(getAccess(roles, "TASK_HEAD") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.ALL_TASKS"),
              link: ALL_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_VIEW_OWN_TASK") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.OWN_TASKS"),
              link: OWN_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_VIEW_OWN_TASK") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.SHARED_TASKS"),
              link: SHARED_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_VIEW_OWN_TASK") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.ASSIGNED_TASKS"),
              link: ASSIGNED_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_VIEW_OWN_TASK") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.SHARED_SUB_TASKS"),
              link: SHARED_SUB_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_VIEW_OWN_TASK") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.ASSIGNED_SUB_TASKS"),
              link: ASSIGNED_SUB_TASKS,
            }),
          },
          {
            ...(getAccess(roles, "TASK_HEAD") && {
              name: t("SIDEBAR.TASK_MANAGEMENT.ARCHIVED_TASKS"),
              link: ARCHIVED_TASKS,
            }),
          },
        ],
      },
      {
        ...(getAccess(roles, "TASK_HEAD") && {
          icon: <IconSettings04 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.TASK_MANAGEMENT.SETTINGS"),
          link: TASK_MANAGEMENT_SETTINGS,
        }),
      },
    ],

    TICKETS: [
      {
        icon: <IconTicket02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.TICKETS.E_TICKETS.LABEL"),

        subMenu: [
          {
            ...(getAccess(roles, "IT_HEAD") && {
              name: t("SIDEBAR.TICKETS.E_TICKETS.ALL_TICKETS"),
              link: E_TICKETS,
            }),
          },
          {
            ...(getAccess(roles, "E_TICKET_VIEW_MY_TICKET") && {
              name: t("SIDEBAR.TICKETS.E_TICKETS.MY_TICKETS"),
              link: MY_TICKETS,
            }),
          },
          {
            ...(getAccess(roles, "IT_HEAD") && {
              name: t("SIDEBAR.TICKETS.E_TICKETS.REQUESTED_TICKETS"),
              link: REQUESTED_TICKETS,
            }),
          },
          {
            ...(getAccess(roles, "IT_EMPLOYEE") && {
              name: t("SIDEBAR.TICKETS.E_TICKETS.ASSIGNED_TICKETS"),
              link: ASSIGNED_TICKETS,
            }),
          },
        ],
      },
      {
        ...(getAccess(roles, "IT_HEAD") && {
          icon: <IconSettings04 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.TICKETS.SETTINGS"),
          link: SETTINGS,
        }),
      },
    ],

    ITS: [
      {
        icon: <IconCode02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.SOFTWARE_INVENTORY"),
        link: SOFTWARE_INVENTORY,
      },

      {
        ...(getAccess(roles, "INVENTORY_STOCK_PAGE") && {
          icon: <IconFileAttachment02 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.IT_INVENTORY.HARDWARE_INVENTORY"),
          link: STOCK,
        }),
      },

      {
        icon: <IconInbox01 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.REQUISITION"),
        link: REQUISITION,
      },

      {
        ...(getAccess(roles, "INVENTORY_WARRANTY_PAGE") && {
          icon: <IconShield01 fill="subtitle2" size="24" />,
          name: t("SIDEBAR.INVENTORY.WARRANTY"),
          link: WARRANTY,
        }),
      },

      {
        icon: <IconTool02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.INVENTORY.MAINTENANCE"),
        link: MAINTENANCE,
      },
    ],
  };

  return {
    bodyTop: [
      {
        icon: <IconHome02 fill="subtitle2" size="24" />,
        name: t("SIDEBAR.DASHBOARD"),
        link: HOME,
      },
      ...(moduleMenuMap[currentModule] || []),
    ],
  };
};

export const CustomSidebar = (): JSX.Element => {
  const navigate = useNavigate();
  const localModule = getStorage("module");
  const initialModule = localModule ? localModule : "";
  const [currentModule, setCurrentModule] = useState<string>(initialModule);

  const dashboardContext = useContext(DashboardContext);

  const { value } = dashboardContext as DashboardContextType;

  const { language, changeLanguage } = useLanguage();
  const { t } = useTranslation();

  const { register, control, setValue } = useForm();

  const { logout } = useAuth();
  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  // set active tab
  const location = useLocation();
  const activeTabName = getNameByPath(
    location?.pathname,
    body(t, roles, currentModule).bodyTop,
  )?.name;

  // const header = {
  //   logo: <img src={LOGO} alt="brand" />,
  // };

  const footer = {
    title: `${language === LANGUAGE.ENGLISH ? "Sign out" : "সাইন আউট"}`,
    onClick: () => {
      removeStorage("module");
      logout({ redirectUri: window.location.origin });
    },
    icon: <IconLogOut01 size="20" fill="dark" />,
  };

  const handleModuleSelection = (selectedModule: string) => {
    setCurrentModule(selectedModule as string);
    setValue("MODULE_SELECT", selectedModule as string);
    setStorage("module", selectedModule);
    navigate(ROUTES.HOME);
  };

  useEffect(() => {
    if (value) {
      setCurrentModule(value);
      setValue("MODULE_SELECT", value);
      setStorage("module", value);
    }
  }, [value]);

  return (
    <>
      <Sidebar
        Link={Link}
        header={getLogo()}
        className="module-select"
        topComponent={
          <Controller
            name="MODULE_SELECT"
            control={control}
            render={() => {
              return (
                <Select
                  key="MODULE_SELECT"
                  {...register("MODULE_SELECT")}
                  options={modulesMapper(modules, roles).map((module) => {
                    return {
                      ...module,
                      label: t(module.label),
                      customComponent: (
                        <div
                          className="d-flex align-items-center gap-6 ps-8 py-8 rounded-3"
                          style={{
                            backgroundImage: `url("${module.bgImage}")`,
                            backgroundSize: "110% 130%",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }}
                        >
                          <img
                            src={module.icon}
                            width="22px"
                            height="22px"
                            alt="module-type"
                          />
                          <Text color="white" weight="bold" size="md">
                            {t(module.label)}
                          </Text>
                        </div>
                      ),
                    };
                  })}
                  defaultValue={currentModule as string}
                  onSelectItem={(e) => {
                    handleModuleSelection(e as string);
                  }}
                  size="xs"
                  status="info"
                  suffix={<IconChevronDown size="16" />}
                  isRequiredSelection
                />
              );
            }}
          />
        }
        extensions={
          <div className="d-flex flex-column gap-9">
            {/* <Link to={PROFILE} className="text-decoration-none text-light">
              <UserInfoCard
                color="white"
                weight="bold"
                title={employeeName}
                subtitle={email}
                imageURL={employeeImageUrl}
                avatarName={employeeName}
                className="bg-info p-8 text-danger rounded-6 overflow-hidden"
              />
            </Link> */}
            <div className="d-flex gap-9 p-4 align-items-center ">
              <Switch
                id="language"
                name="language"
                controlling
                checked={language !== LANGUAGE.ENGLISH}
                onChange={(e) =>
                  changeLanguage(
                    e.target.checked ? LANGUAGE.BANGLA : LANGUAGE.ENGLISH,
                  )
                }
                size="sm"
              />
              <Text
                weight="bold"
                color="primary"
                component="h4"
                sizeType="display"
              >
                {language === LANGUAGE.ENGLISH ? "English" : "বাংলা"}
              </Text>
            </div>
          </div>
        }
        bodyTop={body(t, roles, currentModule).bodyTop}
        footer={footer}
        activeNavItem={activeTabName}
        customClass={{
          navItemSelectedColor: "nav-item-selected rounded-4 ",
          navItemColor: "nav-item-color rounded-4 ",
          navItemHoverColor: "nav-item-hover-color",
        }}
        bgColor="bg-gradient-generic"
      />
    </>
  );
};
